import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  AdapterDateFns
} from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { fr } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  Timestamp,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const LeaveRequest = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [type, setType] = useState('');
  const [motif, setMotif] = useState('');
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [touteLaJournee, setTouteLaJournee] = useState(true);

  useEffect(() => {
    if (!currentUser?.uid) return;

    // Écouter les demandes de congé de l'utilisateur
    const requestsQuery = query(
      collection(db, 'absences'),
      where('personnelId', '==', currentUser.uid),
      where('nature', '==', 'demande'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(requestsQuery, (snapshot) => {
      const requestsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRequests(requestsData);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const calculateHours = (start, end) => {
    if (!start || !end) return null;
    const startParts = start.split(':');
    const endParts = end.split(':');
    const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
    const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
    return ((endMinutes - startMinutes) / 60).toFixed(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!startDate || !endDate || !type || !motif) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    if (!touteLaJournee && (!startTime || !endTime)) {
      alert('Veuillez remplir les heures de début et de fin');
      return;
    }

    if (!userData?.chefServiceId) {
      alert('Impossible de soumettre la demande : chef de service non assigné');
      return;
    }

    try {
      setLoading(true);

      // Créer la demande de congé
      const absenceData = {
        personnelId: currentUser.uid,
        chefServiceId: userData.chefServiceId,
        dateDebut: Timestamp.fromDate(startDate),
        dateFin: Timestamp.fromDate(endDate),
        heureDebut: touteLaJournee ? null : (startTime || null),
        heureFin: touteLaJournee ? null : (endTime || null),
        type,
        motif,
        statut: 'en attente',
        nature: 'demande',
        createdAt: Timestamp.now(),
        establishmentId: userData.establishmentId,
        touteLaJournee: touteLaJournee,
        nombreHeures: touteLaJournee ? null : (startTime && endTime ? calculateHours(startTime, endTime) : null),
        // Ajouter les informations du personnel pour l'affichage
        nomPersonnel: userData.firstName || '',
        prenomPersonnel: userData.lastName || '',
        nomComplet: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        service: userData.service || ''
      };

      await addDoc(collection(db, 'absences'), absenceData);

      // Réinitialiser le formulaire
      setStartDate(null);
      setEndDate(null);
      setStartTime('');
      setEndTime('');
      setType('');
      setMotif('');
      setTouteLaJournee(true);
      alert('Demande de congé envoyée avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande:', error);
      alert('Erreur lors de l\'envoi de la demande: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    if (timestamp instanceof Timestamp) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR');
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString('fr-FR');
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR');
    }
    return '';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/personnel/dashboard')}
        sx={{ mb: 2 }}
        variant="outlined"
      >
        Retour au tableau de bord
      </Button>

      <Typography variant="h5" component="h1" gutterBottom>
        Demande de Congé
      </Typography>


      <Paper sx={{ p: 3, mb: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                <DatePicker
                  label="Date de début"
                  value={startDate}
                  onChange={setStartDate}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                <DatePicker
                  label="Date de fin"
                  value={endDate}
                  onChange={setEndDate}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={touteLaJournee}
                    onChange={(e) => {
                      setTouteLaJournee(e.target.checked);
                      if (e.target.checked) {
                        setStartTime('');
                        setEndTime('');
                      }
                    }}
                    color="primary"
                  />
                }
                label="Absence pour toute la journée"
              />
            </Grid>
            
            {!touteLaJournee && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Heure de début"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      step: 300, // 5 min
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Heure de fin"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      step: 300, // 5 min
                    }}
                    required
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Type de congé</InputLabel>
                <Select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  label="Type de congé"
                >
                  <MenuItem value="congé annuel">Congé annuel</MenuItem>
                  <MenuItem value="congé maladie">Congé maladie</MenuItem>
                  <MenuItem value="congé exceptionnel">Congé exceptionnel</MenuItem>
                  <MenuItem value="récupération">Récupération</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Motif"
                multiline
                rows={4}
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Typography variant="h6" gutterBottom>
        Mes demandes de congé
      </Typography>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date de début</TableCell>
              <TableCell>Date de fin</TableCell>
              <TableCell>Heures</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Motif</TableCell>
              <TableCell>Statut</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>{formatDate(request.dateDebut)}</TableCell>
                <TableCell>{formatDate(request.dateFin)}</TableCell>
                <TableCell>
                  {request.heureDebut && request.heureFin 
                    ? `${request.heureDebut} - ${request.heureFin}`
                    : 'Journée complète'
                  }
                </TableCell>
                <TableCell>{request.type}</TableCell>
                <TableCell>{request.motif}</TableCell>
                <TableCell>
                  <Chip
                    label={request.statut}
                    color={getStatusColor(request.statut)}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case 'approuvée':
      return 'success';
    case 'refusée':
      return 'error';
    default:
      return 'warning';
  }
};

export default LeaveRequest;
